/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  IframeHTMLAttributes,
  useCallback,
  useLayoutEffect,
  useMemo
} from 'react'
import { getMergedClassNames, getUniqueKey } from '../../utils/HTMLutils'
import './VideoPlayer.scss'
const rootClassName = 'component-video-player'

export interface VideoPlayerProps {
  id?: string
  url?: string
  blackGround?: boolean
  className?: string
  iframeProps?: IframeHTMLAttributes<HTMLIFrameElement>
  formatUrl?: boolean
  blockRedirect?: boolean
  autoPlay?: boolean
  callBackStarted?: (id: string) => void
  callBackPaused?: (id: string) => void
  callBackStopped?: (id: string) => void
}

declare global {
  interface Window {
    onYouTubeIframeAPIReady?: (pid?: any) => void
    YT?: any
  }
}
export default function VideoPlayer(props: VideoPlayerProps) {
  const id = useMemo(() => (props.id ? props.id : getUniqueKey()), [props.id])
  const youtubeRegex = /youtu(\.){0,1}be/
  let alreadyPlayed = false
  let player: any

  const handleStateChange = useCallback((newState: number) => {
    switch (newState) {
      case -1:
        // -1 – não iniciado
        if (alreadyPlayed && typeof props.callBackStopped === `function`) {
          props.callBackStopped(id)
        }
        break
      case 0:
        // 0 – encerrado
        if (typeof props.callBackStopped === 'function') {
          props.callBackStopped(id)
        }
        break
      case 1:
        // 1 – em reprodução
        alreadyPlayed = true
        if (typeof props.callBackStarted === 'function') {
          props.callBackStarted(id)
        }
        break
      case 2:
        // 2 – em pausa
        if (typeof props.callBackPaused === 'function') {
          props.callBackPaused(id)
        }
        break
      case 3:
        // 3 – armazenando em buffer
        break
      case 5:
        // 5 – vídeo indicado
        break
    }
  }, [])

  const getProps = () => {
    const p: any = {
      ...props,
      className: getMergedClassNames([
        rootClassName,
        props.className,
        props.blackGround ? 'blackground' : ''
      ])
    }
    delete p.id
    delete p.url
    delete p.blackGround
    delete p.formatUrl
    delete p.iframeProps
    delete p.blockRedirect
    delete p.callBackStarted
    delete p.callBackPaused
    delete p.callBackStopped
    return p
  }

  const getYouTubeID = (url: string) => {
    let ID = ''
    let _url = url
      .replace(/(>|<)/gi, '')
      .split(/(vi\/|v=|\/v\/|youtu\.be\/|\/embed\/)/)

    if (_url[2] !== undefined) {
      const _ID = _url[2].split(/[^0-9a-z_-]/i)
      ID = _ID[0]
    } else {
      ID = url
    }
    if (props.autoPlay && !ID.match(/autoplay/)) {
      ID = ID.concat(ID.match(/\?/) ? '&' : '?').concat(
        `autoplay=${props.autoPlay ? '1' : '0'}`
      )
    }

    if (!ID.match(/enablejsapi=/)) {
      ID = ID.concat(ID.match(/\?/) ? '&' : '?').concat(
        'enablejsapi=1&origin=' + window.origin
      )
    }

    return ID
  }

  const getMicrosoftStreamID = (url: string) => {
    return String(url).replace(
      /(.*microsoftstream.com)(\/embed){0,1}\/video\/(.+)/,
      '$3'
    )
  }

  const getVimeoID = (url: string) => {
    const regex = /(.*)(vimeo\.com\/){1}(video\/){0,1}(.+)(\/){0,1}/
    return String(url).replace(regex, '$4')
  }

  const source = useMemo(() => {
    if (!props.url) return ''
    if (props.formatUrl && String(props.url).match(youtubeRegex)) {
      return `https://www.youtube.com/embed/${getYouTubeID(props.url)}`
    } else if (
      props.formatUrl &&
      String(props.url).match(/(.*microsoftstream.com)(\/embed){0,1}\/video/)
    ) {
      return `https://web.microsoftstream.com/embed/video/${getMicrosoftStreamID(
        props.url
      )}?autoplay=${props.autoPlay ? 'true' : 'false'}&amp;showinfo=true`
    } else if (
      props.formatUrl &&
      String(props.url).match(/.*vimeo.com(\/video\/{0,1}){0,1}/)
    ) {
      return `https://player.vimeo.com/video/${getVimeoID(props.url)}`
    }
    return props.url
  }, [props.formatUrl, props.url])

  useLayoutEffect(() => {
    if (props.url!?.match(youtubeRegex)) {
      // Chama a inicialização do nosso iFrame
      const callEm = () => {
        if (window.YT && window.YT.Player) {
          player = new window.YT.Player(id, {
            events: {
              onStateChange: ({ data }: any) => handleStateChange(data)
            }
          })
          //Este código é necessário para remover o warning
          if (!player) {
            return
          }
        }
      }

      // Testa se a YT Api está sendo importada
      if (
        !document.querySelector(
          'script[src="https://www.youtube.com/iframe_api"]'
        )
      ) {
        // Inicia a API do Youtube caso não esteja iniciada
        var tag = document.createElement('script')
        tag.src = 'https://www.youtube.com/iframe_api'
        var firstScriptTag = document.getElementsByTagName('script')[0]
        firstScriptTag.parentNode!.insertBefore(tag, firstScriptTag)
        window.onYouTubeIframeAPIReady = () => {
          callEm()
        }
      } else {
        callEm()
      }
    }
  }, [])

  return (
    <div {...getProps()}>
      <iframe
        id={id}
        title={`player${getUniqueKey()}`}
        frameBorder='0'
        allowFullScreen
        {...(props.blockRedirect && {
          sandbox:
            'allow-forms allow-scripts allow-pointer-lock allow-same-origin allow-top-navigation'
        })}
        allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
        {...props.iframeProps}
        src={source}
      />
    </div>
  )
}

VideoPlayer.defaultProps = {
  formatUrl: true
}
